import React, { useState, useEffect } from 'react';
import './TrainingAcknowledgement.css';

/**
 * Training Acknowledgement Modal
 * 
 * Shows on first login and requires user to acknowledge they've reviewed
 * or will review the training documentation before continuing.
 * 
 * Stores acknowledgement in localStorage with email + timestamp.
 */
function TrainingAcknowledgement({ user, onAcknowledge }) {
  const [show, setShow] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    // Check if user has already acknowledged training
    const storageKey = `training_acknowledged_${user?.email}`;
    const hasAcknowledged = localStorage.getItem(storageKey);
    
    if (!hasAcknowledged) {
      setShow(true);
    }
  }, [user]);

  const handleAcknowledge = () => {
    if (!acknowledged) return;

    // Store acknowledgement with timestamp
    const storageKey = `training_acknowledged_${user?.email}`;
    const acknowledgementData = {
      email: user?.email,
      name: user?.full_name,
      timestamp: new Date().toISOString(),
      acknowledged: true
    };
    
    localStorage.setItem(storageKey, JSON.stringify(acknowledgementData));
    
    setShow(false);
    if (onAcknowledge) {
      onAcknowledge(acknowledgementData);
    }
  };

  if (!show) return null;

  return (
    <div className="training-ack-overlay">
      <div className="training-ack-modal">
        <div className="training-ack-header">
          <h2>📚 Welcome to SimplMeds Review</h2>
          <p className="training-ack-subtitle">First-time Training & Documentation</p>
        </div>

        <div className="training-ack-content">
          <div className="training-intro">
            <p>
              Before you begin reviewing medications, please familiarize yourself with the 
              SimplMeds Review Application training documentation.
            </p>
            <p>
              <strong>As a pharmacist reviewer, you are responsible for:</strong>
            </p>
            <ul>
              <li>Ensuring simplified medication content is medically accurate</li>
              <li>Verifying all critical safety information is included</li>
              <li>Maintaining patient-friendly language (8th grade reading level)</li>
              <li>Following the review workflow and approval process</li>
            </ul>
          </div>

          <div className="training-docs-grid">
            <a 
              href="/docs/reviewer-guide.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="training-doc-card critical"
            >
              <div className="doc-icon">📋</div>
              <div className="doc-info">
                <h3>Pharmacist Reviewer Guide</h3>
                <p>Comprehensive training for medication review workflow</p>
                <span className="doc-badge badge-critical">Required Reading</span>
              </div>
            </a>

            <a 
              href="/docs/quick-reference.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="training-doc-card"
            >
              <div className="doc-icon">⚡</div>
              <div className="doc-info">
                <h3>Quick Reference Card</h3>
                <p>1-page cheat sheet for your desk</p>
                <span className="doc-badge badge-recommended">Recommended</span>
              </div>
            </a>

            <a 
              href="/docs/troubleshooting.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="training-doc-card"
            >
              <div className="doc-icon">🔧</div>
              <div className="doc-info">
                <h3>Troubleshooting Guide</h3>
                <p>Solutions to common issues</p>
                <span className="doc-badge badge-reference">Reference</span>
              </div>
            </a>
          </div>

          <div className="training-help-note">
            <p>
              💡 <strong>Need help later?</strong> Access all training documentation anytime 
              from the <strong>📚 Help & Training</strong> link in the sidebar.
            </p>
          </div>

          <div className="training-ack-checkbox">
            <label>
              <input 
                type="checkbox" 
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span className="checkbox-text">
                I acknowledge that I have reviewed (or will review before starting work) the 
                <strong> Pharmacist Reviewer Guide</strong> and understand my responsibilities 
                as a SimplMeds reviewer.
              </span>
            </label>
          </div>

          <div className="training-footer-note">
            <p>
              <strong>Important:</strong> This acknowledgement will be stored with your email 
              ({user?.email}) and timestamp for training compliance records.
            </p>
          </div>
        </div>

        <div className="training-ack-footer">
          <button 
            className="btn-sign-off"
            disabled={!acknowledged}
            onClick={handleAcknowledge}
          >
            ✅ Sign Off & Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrainingAcknowledgement;
